import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Notif {
  id: string;
  message: string;
  read: boolean;
  created_at: string;
  review_id: string | null;
}

const NotificationsBell = ({ userId }: { userId: string }) => {
  const [items, setItems] = useState<Notif[]>([]);
  const [open, setOpen] = useState(false);

  const fetchItems = async () => {
    const { data } = await supabase
      .from("notifications" as any)
      .select("id, message, read, created_at, review_id")
      .order("created_at", { ascending: false })
      .limit(20);
    setItems((data as any as Notif[]) || []);
  };

  useEffect(() => {
    fetchItems();
    const channel = supabase
      .channel("notif-" + userId)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        () => fetchItems()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const unread = items.filter((i) => !i.read).length;

  const markAllRead = async () => {
    await supabase
      .from("notifications" as any)
      .update({ read: true } as any)
      .eq("user_id", userId)
      .eq("read", false);
    fetchItems();
  };

  const markRead = async (id: string) => {
    await supabase.from("notifications" as any).update({ read: true } as any).eq("id", id);
    fetchItems();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative text-muted-foreground">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline flex items-center gap-1">
              <Check className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground text-center">No notifications yet.</div>
        ) : (
          <ul>
            {items.map((n) => (
              <li key={n.id} className={`border-b last:border-b-0 ${!n.read ? "bg-muted/40" : ""}`}>
                <Link
                  to="/activity"
                  onClick={() => { markRead(n.id); setOpen(false); }}
                  className="block px-3 py-2 hover:bg-muted transition-colors"
                >
                  <p className="text-xs text-foreground">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
